import { docSubtree } from '../../knowledge/archive';
import { docAncestors, docChildren, docDepth, docSubtreeHeight, isAdmin } from '../../knowledge/tree';
import { richDocFrom, richDocToText } from '../../rich-doc';
import { isPersonalAttachment } from '../../storage-folders/system';
import type { RichDoc } from '../../rich-doc';
import { DOC_MAX_DEPTH } from '../../types';
import type { AttachmentId, KnowledgeDoc, KnowledgeDocId, MockCache, UserId } from '../../types';

import { maxSortKey, sortKeyBetween } from '../internal';
import { bump, requireCache } from '../runtime';

const SHARE_TOKEN_MIN_LENGTH = 16;

function requireDoc(cache: MockCache, id: KnowledgeDocId): KnowledgeDoc {
  const doc = cache.docs.get(id);
  if (doc === undefined) {
    throw new Error('Документ не найден');
  }
  return doc;
}

function requireEditable(doc: KnowledgeDoc): void {
  if (doc.archivedAt !== null) {
    throw new Error('Документ в архиве');
  }
  if (doc.editLocked) {
    throw new Error('Документ заблокирован для редактирования');
  }
}

function requireLiveParent(cache: MockCache, parentId: KnowledgeDocId | null): void {
  if (parentId === null) {
    return;
  }
  const parent = requireDoc(cache, parentId);
  if (parent.archivedAt !== null) {
    throw new Error('Родительский документ в архиве');
  }
}

function requireDepthWithin(parentId: KnowledgeDocId | null, cache: MockCache, subtreeHeight: number): void {
  const parentDepth = parentId === null ? 0 : docDepth(cache, parentId);
  if (parentDepth + subtreeHeight > DOC_MAX_DEPTH) {
    throw new Error(`Вложенность документов не может быть глубже ${DOC_MAX_DEPTH} уровней`);
  }
}

function touch(cache: MockCache, doc: KnowledgeDoc): void {
  doc.updatedAt = cache.nowMs();
  bump('docs', `doc:${String(doc.id)}`);
}

export function createDoc(args: { id: KnowledgeDocId; parentId: KnowledgeDocId | null; title: string }): KnowledgeDoc {
  const cache = requireCache();
  if (cache.docs.has(args.id)) {
    throw new Error('Документ с таким идентификатором уже есть');
  }
  requireLiveParent(cache, args.parentId);
  requireDepthWithin(args.parentId, cache, 1);
  const now = cache.nowMs();
  const siblings = docChildren(cache, args.parentId).map((doc) => doc.sortKey);
  const doc: KnowledgeDoc = {
    id: args.id,
    workspaceId: args.parentId === null ? cache.activeWorkspaceId : requireDoc(cache, args.parentId).workspaceId,
    parentId: args.parentId,
    title: args.title,
    bodyJson: [],
    bodyText: '',
    ownerId: cache.me,
    creatorId: cache.me,
    editLocked: false,
    shareToken: null,
    coverAttachmentId: null,
    sortKey: sortKeyBetween(maxSortKey(siblings), null),
    archivedAt: null,
    createdAt: now,
    updatedAt: now,
  };
  cache.docs.set(doc.id, doc);
  bump('docs', `doc:${String(doc.id)}`);
  return doc;
}

export function renameDoc(args: { id: KnowledgeDocId; title: string }): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  requireEditable(doc);
  doc.title = args.title;
  touch(cache, doc);
}

export function setDocBody(args: { id: KnowledgeDocId; bodyJson: RichDoc }): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  requireEditable(doc);
  doc.bodyJson = richDocFrom(args.bodyJson);
  doc.bodyText = richDocToText(doc.bodyJson);
  touch(cache, doc);
}

function placementKeys(
  cache: MockCache,
  doc: KnowledgeDoc,
  parentId: KnowledgeDocId | null,
  afterId: KnowledgeDocId | null,
): [string | null, string | null] {
  const siblings = docChildren(cache, parentId).filter((sibling) => sibling.id !== doc.id);
  if (afterId === null) {
    return [null, siblings[0]?.sortKey ?? null];
  }
  const at = siblings.findIndex((sibling) => sibling.id === afterId);
  if (at < 0) {
    throw new Error('Соседний документ не найден среди документов нового родителя');
  }
  return [siblings[at]?.sortKey ?? null, siblings[at + 1]?.sortKey ?? null];
}

export function moveDoc(args: {
  id: KnowledgeDocId;
  parentId: KnowledgeDocId | null;
  afterId: KnowledgeDocId | null;
}): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  requireEditable(doc);
  requireLiveParent(cache, args.parentId);
  const intoOwnSubtree =
    args.parentId !== null &&
    (args.parentId === doc.id || docAncestors(cache, args.parentId).some((ancestor) => ancestor.id === doc.id));
  if (intoOwnSubtree) {
    throw new Error('Документ нельзя перенести в самого себя или в свой вложенный документ');
  }
  requireDepthWithin(args.parentId, cache, docSubtreeHeight(cache, doc.id));
  const [lower, upper] = placementKeys(cache, doc, args.parentId, args.afterId);
  doc.parentId = args.parentId;
  doc.sortKey = sortKeyBetween(lower, upper);
  touch(cache, doc);
}

export function setDocOwner(args: { id: KnowledgeDocId; ownerId: UserId }): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  const owner = cache.users.get(args.ownerId);
  if (owner === undefined || !owner.active) {
    throw new Error('Владелец не найден или отключён');
  }
  doc.ownerId = args.ownerId;
  touch(cache, doc);
}

export function setDocEditLocked(args: { id: KnowledgeDocId; locked: boolean }): void {
  const cache = requireCache();
  if (!isAdmin(cache)) {
    throw new Error('Блокировку редактирования переключают только администраторы');
  }
  const doc = requireDoc(cache, args.id);
  doc.editLocked = args.locked;
  touch(cache, doc);
}

export function setDocSharing(args: { id: KnowledgeDocId; enabled: boolean; token: string | null }): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  if (!args.enabled) {
    doc.shareToken = null;
    touch(cache, doc);
    return;
  }
  const token = args.token ?? '';
  if (token.length < SHARE_TOKEN_MIN_LENGTH) {
    throw new Error('Ключ доступа по ссылке слишком короткий');
  }
  for (const other of cache.docs.values()) {
    if (other.id !== doc.id && other.shareToken === token) {
      throw new Error('Такой ключ доступа по ссылке уже занят');
    }
  }
  doc.shareToken = token;
  touch(cache, doc);
}

export function setDocCover(args: { id: KnowledgeDocId; attachmentId: AttachmentId | null }): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  requireEditable(doc);
  if (args.attachmentId !== null) {
    const attachment = cache.attachments.get(args.attachmentId);
    if (attachment === undefined) {
      throw new Error('Вложение не найдено');
    }
    if (isPersonalAttachment(attachment)) {
      throw new Error('Личный файл нельзя поставить обложкой');
    }
    if (!attachment.mime.startsWith('image/')) {
      throw new Error('Обложкой может быть только изображение');
    }
  }
  doc.coverAttachmentId = args.attachmentId;
  touch(cache, doc);
}

function subtreeOf(cache: MockCache, doc: KnowledgeDoc): KnowledgeDoc[] {
  return [doc, ...docChildren(cache, doc.id).flatMap((child) => subtreeOf(cache, child))];
}

export function archiveDoc(args: { id: KnowledgeDocId }): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  if (doc.archivedAt !== null) {
    return;
  }
  const subtree = subtreeOf(cache, doc);
  if (subtree.some((member) => member.editLocked)) {
    throw new Error('В архив нельзя убрать документ, пока он или вложенный в него заблокирован для редактирования');
  }
  const now = cache.nowMs();
  for (const member of subtree) {
    member.archivedAt = now;
    member.updatedAt = now;
    bump(`doc:${String(member.id)}`);
  }
  bump('docs');
}

export function restoreDoc(args: { id: KnowledgeDocId }): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  const archivedAt = doc.archivedAt;
  if (archivedAt === null) {
    return;
  }
  const batch = docSubtree(cache, doc, (member) => member.archivedAt === archivedAt);
  const parent = doc.parentId === null ? undefined : cache.docs.get(doc.parentId);
  if (doc.parentId !== null && (parent === undefined || parent.archivedAt !== null)) {
    const rootSortKeys = docChildren(cache, null).map((root) => root.sortKey);
    doc.parentId = null;
    doc.sortKey = sortKeyBetween(maxSortKey(rootSortKeys), null);
  }
  const now = cache.nowMs();
  for (const member of batch) {
    member.archivedAt = null;
    member.updatedAt = now;
    bump(`doc:${String(member.id)}`);
  }
  bump('docs');
}

export function deleteDoc(args: { id: KnowledgeDocId }): void {
  const cache = requireCache();
  const doc = requireDoc(cache, args.id);
  if (doc.archivedAt === null) {
    throw new Error('Окончательно удалить можно только документ из архива');
  }
  for (const member of docSubtree(cache, doc)) {
    cache.docs.delete(member.id);
    bump(`doc:${String(member.id)}`);
  }
  bump('docs');
}
